import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';

interface Props {
  /** Plain language. Never a raw status code, never a stack trace (§8). */
  message?: string;
  onRetry?: () => void;
  /**
   * The heading level of the state's title.
   *
   * ⚠️ **The title was a `<p>`, so the state carried no heading at all** (§9, found in a live
   * browser 2026-07-25): a screen reader landing on a failure heard a sentence and a button, with
   * nothing to orient by. It is `2` here because the state usually sits inside a screen that keeps
   * its own `h1` — the dashboard's error state renders under *"Dashboard"* — and `1` where the
   * state **is** the page and there is no screen title above it (`RouteError`).
   */
  headingLevel?: 1 | 2;
}

/** State 3 of `DESIGN.md` §8: what failed, in words, and a way to try again. */
export function ErrorState({ message, onRetry, headingLevel = 2 }: Props) {
  const { t } = useTranslation(['components', 'common']);
  const Title = headingLevel === 1 ? 'h1' : 'h2';

  return (
    <div className="flex flex-col items-center gap-3 rounded border border-rule bg-paper px-8 py-12 text-center">
      <AlertTriangle size={20} strokeWidth={1.5} className="text-negative" aria-hidden />
      <Title className="text-sm font-medium text-ink-900">{t('components:state.errorTitle')}</Title>
      <p className="text-sm text-ink-700">{message ?? t('components:state.errorBody')}</p>
      {onRetry && (
        <Button variant="secondary" onClick={onRetry}>
          {t('common:retry')}
        </Button>
      )}
    </div>
  );
}
